/**
 * Mobile-only top bar shown on every page.
 * Renders the LexIQ wordmark + auth buttons (Login/Signup) for guests,
 * or the lawyer's name + logout for authenticated users.
 *
 * On lg+ the existing desktop Layout header handles this; this component
 * hides itself with `lg:hidden`.
 */
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { SignOut, UserCircle, Scales, Shield } from "@phosphor-icons/react";
import { useUser } from "../context/UserAuthContext";
import { useAdmin } from "../context/AdminAuthContext";
import LexiqWordmark from "./LexiqWordmark";

export default function MobileTopBar() {
  const { user, logout } = useUser();
  const { admin } = useAdmin();
  const nav = useNavigate();
  const location = useLocation();

  // Pages with their own integrated mobile headers don't need the global top bar.
  // Includes dynamic-id variants (e.g. /lawyers/:slug, /messages/:id, /portal/:token).
  const HIDE_TOP_BAR_ON = ["/", "/chat", "/cases", "/cases/new", "/cassation", "/timeline", "/analyze", "/draft", "/clients", "/laws", "/internal-regulations", "/courts", "/achievements", "/leaderboard", "/messages", "/account", "/account/agreement", "/lawyers", "/settings", "/help", "/about"];
  const HIDE_TOP_BAR_PREFIX = ["/lawyers/", "/messages/", "/portal/", "/p/", "/account/", "/cases/"];
  if (HIDE_TOP_BAR_ON.includes(location.pathname)) return null;
  if (HIDE_TOP_BAR_PREFIX.some((p) => location.pathname.startsWith(p))) return null;

  const onLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  return (
    <header
      className="lg:hidden sticky top-0 z-40 border-b border-[#EAEAEA]"
      dir="rtl"
      data-testid="mobile-topbar"
      style={{
        paddingTop: "env(safe-area-inset-top, 0px)",
        background: "rgba(255,255,255,0.1)",
        backdropFilter: "blur(22px) saturate(180%)",
        WebkitBackdropFilter: "blur(22px) saturate(180%)",
      }}
    >
      <div className="flex items-center justify-between px-4 h-14">
        <NavLink to="/" className="flex items-center gap-2" data-testid="mobile-brand">
          <Scales size={20} weight="duotone" className="text-[#D4AF37]" />
          <span className="font-amiri text-lg text-[#0A192F] leading-none"><LexiqWordmark className="text-[18px]" testid="topbar-lexiq" /></span>
        </NavLink>

        {user ? (
          <div className="flex items-center gap-2">
            {admin && (
              <NavLink
                to="/admin"
                data-testid="mobile-admin-link"
                className="p-1.5 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white transition"
                aria-label="لوحة الإدارة"
              >
                <Shield size={14} weight="duotone" />
              </NavLink>
            )}
            <div
              className="flex items-center gap-1 text-xs text-[#0A192F] max-w-[120px] truncate"
              data-testid="mobile-user-name"
            >
              <UserCircle size={16} weight="duotone" className="text-[#D4AF37] shrink-0" />
              <span className="truncate">{user.name}</span>
            </div>
            <button
              onClick={onLogout}
              data-testid="mobile-logout-btn"
              className="p-1.5 border border-[#EAEAEA] hover:bg-[#0A192F] hover:text-white transition"
              aria-label="تسجيل خروج"
            >
              <SignOut size={14} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <NavLink
              to="/login"
              data-testid="mobile-login-btn"
              className="px-3 py-1.5 border border-[#0A192F] text-[#0A192F] text-xs hover:bg-[#0A192F] hover:text-white transition"
            >
              دخول
            </NavLink>
            <NavLink
              to="/signup"
              data-testid="mobile-signup-btn"
              className="px-3 py-1.5 bg-[#0A192F] text-white text-xs hover:bg-[#D4AF37] hover:text-[#0A192F] transition"
            >
              تسجيل
            </NavLink>
          </div>
        )}
      </div>
    </header>
  );
}
